import { computed, observable } from "mobx";
import FormStore from "~/stores/FormStore";
import TaxStore from "~/stores/Invoice/TaxStore";
import SubscriptionStore from "~/stores/Subscription/SubscriptionStore";
import OtherProductStore from "~/stores/OtherProductStore";
import SubscriptionCancellationStore from "~/stores/Subscription/SubscriptionCancellationStore";

class SubscriptionPageStore {
  @observable subscriptionStore = null;

  @observable otherProductStore = null;

  @observable taxStore = null;

  @observable formStore = null;

  @observable subscriptionCancellationStore = null;

  constructor() {
    this.subscriptionStore = new SubscriptionStore();
    this.otherProductStore = new OtherProductStore();
    this.taxStore = new TaxStore();
    this.formStore = new FormStore();
    this.subscriptionCancellationStore = new SubscriptionCancellationStore();
  }

  calculatePriceForDays(price, days = 0) {
    if (days === 0) {
      return price;
    }

    return Math.round((price / 30) * days * 100) / 100;
  }

  calculateSubtotal(subscriptionItems, otherProducts, days = 0) {
    let total = 0;

    subscriptionItems.forEach(subscriptionItem => {
      const otherProduct = otherProducts.find(item => item.otherProductId === subscriptionItem.otherProductId);

      total += subscriptionItem.quantity * this.calculatePriceForDays(parseFloat(otherProduct.price), days);
    });

    return total;
  }

  calculateTotalAmountWithTaxes(subtotal, applicableTaxes) {
    if (applicableTaxes.length === 0) {
      return subtotal;
    }

    let taxAmount = 0;

    applicableTaxes.forEach(applicableTax => {
      taxAmount += Math.round(subtotal * applicableTax.rate) / 100;
    });

    return subtotal + taxAmount;
  }

  getTotalAmountWithTaxes(subscriptionItems, otherProducts, applicableTaxes, days = 0) {
    const subtotal = this.calculateSubtotal(subscriptionItems, otherProducts, days);

    return this.calculateTotalAmountWithTaxes(subtotal, applicableTaxes);
  }

  getQuantityOptions() {
    const quantityOptions = [];

    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach(number => {
      quantityOptions.push({
        key: number,
        value: number,
        text: number,
      });
    });

    return quantityOptions;
  }

  @computed
  get areFormSubscriptionItemsDirty() {
    const { initialModel, model } = this.formStore;

    if (typeof initialModel.subscriptionItems === "undefined" || typeof model.subscriptionItems === "undefined") {
      return false;
    }

    const { subscriptionItems: initialSubscriptionItems } = initialModel;
    const { subscriptionItems } = model;

    // Same number of subscription items
    if (initialSubscriptionItems.length !== subscriptionItems.length) {
      return true;
    }

    // Compare the data
    for (let i = 0; i < initialSubscriptionItems.length; i += 1) {
      const subscriptionItem = subscriptionItems.find(
        item => item.otherProductId === initialSubscriptionItems[i].otherProductId,
      );

      if (typeof subscriptionItem === "undefined") {
        return true;
      }

      if (subscriptionItem.quantity !== initialSubscriptionItems[i].quantity) {
        return true;
      }
    }

    return false;
  }

  @computed
  get formSubscriptionItemChanges() {
    const { initialModel, model } = this.formStore;

    if (typeof initialModel.subscriptionItems === "undefined" || typeof model.subscriptionItems === "undefined") {
      return [];
    }

    if (!this.areFormSubscriptionItemsDirty) {
      return [];
    }

    const formSubscriptionItemChanges = [];

    const { subscriptionItems: initialSubscriptionItems } = initialModel;
    const { subscriptionItems } = model;

    // Compare the data
    for (let i = 0; i < subscriptionItems.length; i += 1) {
      const initialSubscriptionItem = initialSubscriptionItems.find(
        item => item.otherProductId === subscriptionItems[i].otherProductId,
      );

      // If it wasn't there at the beginning, we will need to charge
      if (typeof initialSubscriptionItem === "undefined") {
        formSubscriptionItemChanges.push({
          otherProductId: subscriptionItems[i].otherProductId,
          quantity: subscriptionItems[i].quantity,
        });

        // eslint-disable-next-line no-continue
        continue;
      }

      if (initialSubscriptionItem.quantity < subscriptionItems[i].quantity) {
        formSubscriptionItemChanges.push({
          otherProductId: subscriptionItems[i].otherProductId,
          quantity: subscriptionItems[i].quantity - initialSubscriptionItem.quantity,
        });
      }
    }

    return formSubscriptionItemChanges;
  }

  @computed
  get creditCardId() {
    const { model } = this.formStore;

    return model.creditCardId !== "" ? parseInt(model.creditCardId, 10) : null;
  }

  getUserForSubscription(providers, customers, subscription) {
    if (subscription.providerId !== null) {
      const provider = providers.find(item => item.providerId === subscription.providerId);
      return typeof provider !== "undefined" ? provider : null;
    }

    const customer = customers.find(item => item.customerId === subscription.customerId);
    return typeof customer !== "undefined" ? customer : null;
  }

  getApplicableTaxesForAddress(taxes, address) {
    return taxes.filter(tax => tax.state === address.state && tax.country === address.country);
  }

  getApplicableTaxesForSubscription(providers, customers, taxes, subscription) {
    const user = this.getUserForSubscription(providers, customers, subscription);

    return this.getApplicableTaxesForAddress(taxes, user.address);
  }
}

export default SubscriptionPageStore;
